import { cn } from '@/lib/utils';
import { HealthcomDiscipline } from '@chiroup/core/types/HealthComLocation.type';
import { RadioGroup } from '@headlessui/react';
import { CheckCircle as CheckCircleIcon } from 'lucide-react';
import React from 'react';

type Props = {
  disciplines: HealthcomDiscipline[];
  onChange: (treatment: number) => void;
  value: number | null;
  providerOptions: any[];
};

const ScheduleTreatments: React.FC<Props> = ({ disciplines, onChange, value, providerOptions }) => {
  const filteredDisciplines = disciplines
    ?.map(discipline => {
      const filteredTreatments = !providerOptions?.length
        ? discipline.treatments
        : discipline.treatments?.filter(t => {
            if (!t?.providers?.length) return true;

            return providerOptions?.some(p => t.providers?.includes(p.ID));
          }) || [];

      return {
        ...discipline,
        treatments: filteredTreatments,
      };
    })
    .filter(discipline => discipline.treatments.length > 0);

  return (
    <RadioGroup value={value} onChange={onChange}>
      <RadioGroup.Label>
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Select a treatment</h3>
      </RadioGroup.Label>

      <div className="mt-4 space-y-6">
        {filteredDisciplines.map(discipline => (
          <div key={discipline.ID} className="mb-4">
            {filteredDisciplines.length > 1 && (
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  {discipline.name}
                </h4>
                {discipline.description && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {discipline.description}
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2.5">
              {discipline.treatments.map(treatment => (
                <RadioGroup.Option
                  key={treatment.ID}
                  value={treatment.ID}
                  className={({ checked, active }) =>
                    cn(
                      'portal-card relative flex cursor-pointer p-4 focus:outline-none transition-shadow',
                      checked ? 'ring-2 ring-[var(--primary-color)]' : '',
                      active ? 'ring-2 ring-[var(--primary-color)]' : ''
                    )
                  }
                >
                  {({ checked }) => (
                    <>
                      <span className="flex flex-1">
                        <span className="flex flex-col">
                          <RadioGroup.Label
                            as="span"
                            className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                          >
                            {treatment.name}{' '}
                            {treatment.onlineBookingDuration &&
                            typeof treatment.treatmentDuration === 'number'
                              ? `(${treatment.treatmentDuration} min)`
                              : ''}
                          </RadioGroup.Label>
                          <RadioGroup.Description
                            as="span"
                            className="mt-1 text-xs text-gray-400 dark:text-gray-500"
                          >
                            {treatment.description}
                          </RadioGroup.Description>
                          {treatment.onlineBookingPrice && (
                            <RadioGroup.Description
                              as="span"
                              className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100"
                            >
                              Cash Price: {treatment.price}
                            </RadioGroup.Description>
                          )}
                        </span>
                      </span>
                      <CheckCircleIcon
                        className={cn(
                          !checked ? 'invisible' : '',
                          'h-5 w-5 text-[var(--primary-color)]'
                        )}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </RadioGroup.Option>
              ))}
            </div>
          </div>
        ))}
      </div>
    </RadioGroup>
  );
};

export default ScheduleTreatments;
